import React, { useState, useEffect } from "react";
import { X, Hash, Save, ArrowRight, User, Clock } from "lucide-react";

export default function InventoryStockModal({
  isOpen,
  onClose,
  item,
  currentUser,
  onSaveStock,
  isSaving = false
}) {
  const [newQuantity, setNewQuantity] = useState(0);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (item) {
      setNewQuantity(Number(item.quantity) || 0);
      setReason("");
    }
  }, [item]);

  if (!isOpen || !item) return null;

  const originalQty = Number(item.quantity) || 0;
  const targetQty = Number(newQuantity) || 0;
  const diff = targetQty - originalQty;
  const userName = currentUser?.displayName || currentUser?.email?.split("@")[0] || "User";

  const handleSubmit = (e) => {
    e.preventDefault();
    onSaveStock(item.id, targetQty, reason.trim());
  };

  const handleStep = (amount) => {
    setNewQuantity((prev) => Math.max(0, Number(prev) + amount));
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1200,
        padding: "1rem"
      }}
      onClick={onClose}
    >
      <div
        className="card glass-card animate-fade-in"
        style={{
          width: "100%",
          maxWidth: "480px",
          backgroundColor: "var(--bg-card)",
          border: "1px solid var(--border-color)",
          borderRadius: "8px",
          overflow: "hidden",
          padding: 0
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div style={{
          padding: "1rem 1.5rem",
          borderBottom: "1px solid var(--border-color)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(10, 12, 18, 0.4)"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Hash size={18} style={{ color: "var(--primary)" }} />
            <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 700, color: "var(--text-primary)" }}>
              Adjust Stock Count
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "var(--text-secondary)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center"
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <div style={{ padding: "1.5rem" }}>
          {/* Item Info Summary */}
          <div style={{
            padding: "0.75rem 1rem",
            backgroundColor: "rgba(255, 255, 255, 0.02)",
            border: "1px solid var(--border-color)",
            borderRadius: "6px",
            marginBottom: "1.25rem"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.3rem" }}>
              <span className="sku-badge" style={{ margin: 0 }}>{item.sku}</span>
              <span style={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}>
                Order Count: <strong style={{ color: "var(--text-primary)" }}>{item.orderCount !== undefined ? item.orderCount : "-"}</strong>
              </span>
            </div>
            <div style={{ fontWeight: 600, color: "var(--text-primary)", fontSize: "0.95rem" }}>
              {item.name}
            </div>
            <div style={{ fontSize: "0.78rem", color: "var(--text-secondary)", marginTop: "0.25rem" }}>
              Location: <strong>{item.warehouse || "NC"}</strong> {item.zone ? `• Zone: ${item.zone}` : ""}
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Quantity Adjuster */}
            <div className="form-group" style={{ marginBottom: "1.25rem" }}>
              <label className="form-label" htmlFor="newQuantityInput" style={{ fontWeight: 600 }}>
                New Stock Count *
              </label>
              
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleStep(-5)}
                  style={{ padding: "0.4rem 0.6rem", fontSize: "0.8rem", minHeight: "auto" }}
                  title="Subtract 5"
                >
                  -5
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleStep(-1)}
                  style={{ padding: "0.4rem 0.6rem", fontSize: "0.8rem", minHeight: "auto" }}
                  title="Subtract 1"
                >
                  -1
                </button>
                
                <input
                  id="newQuantityInput"
                  type="number"
                  min="0"
                  className="form-input text-center"
                  value={newQuantity}
                  onChange={(e) => setNewQuantity(e.target.value === "" ? "" : Number(e.target.value))}
                  onWheel={(e) => e.target.blur()}
                  style={{
                    fontSize: "1.2rem",
                    fontWeight: 700,
                    margin: 0,
                    flex: 1,
                    color: targetQty !== (item.orderCount ?? targetQty) ? "var(--danger)" : "var(--text-primary)"
                  }}
                  required
                />

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleStep(1)}
                  style={{ padding: "0.4rem 0.6rem", fontSize: "0.8rem", minHeight: "auto" }}
                  title="Add 1"
                >
                  +1
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleStep(5)}
                  style={{ padding: "0.4rem 0.6rem", fontSize: "0.8rem", minHeight: "auto" }}
                  title="Add 5"
                >
                  +5
                </button>
              </div>
            </div>

            {/* Before / After Difference Indicator */}
            <div style={{
              padding: "0.6rem 0.8rem",
              borderRadius: "6px",
              backgroundColor: diff !== 0 ? "rgba(59, 130, 246, 0.08)" : "rgba(255, 255, 255, 0.02)",
              border: "1px solid " + (diff !== 0 ? "rgba(59, 130, 246, 0.25)" : "var(--border-color)"),
              marginBottom: "1.25rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: "0.85rem"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span style={{ color: "var(--text-secondary)" }}>Current: <strong>{originalQty}</strong></span>
                <ArrowRight size={14} style={{ color: "var(--text-muted)" }} />
                <span style={{ color: "var(--primary)", fontWeight: 700 }}>New: {targetQty}</span>
              </div>
              <div>
                <span style={{
                  fontWeight: 700,
                  color: diff > 0 ? "var(--success)" : diff < 0 ? "var(--danger)" : "var(--text-muted)"
                }}>
                  {diff > 0 ? `+${diff}` : diff}
                </span>
              </div>
            </div>

            {/* Reason / Note Textarea */}
            <div className="form-group" style={{ marginBottom: "1.25rem" }}>
              <label className="form-label" htmlFor="stockChangeReason" style={{ fontWeight: 600 }}>
                Reason / Note for Stock Change
              </label>
              <textarea
                id="stockChangeReason"
                className="form-input"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Physical inventory recount, damaged units removed..."
                style={{ fontSize: "0.85rem", lineHeight: 1.35 }}
              />
            </div>

            {/* Audit Log Timestamp & User Notice */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: "0.75rem",
              color: "var(--text-muted)",
              marginBottom: "1.25rem",
              borderTop: "1px solid var(--border-color)",
              paddingTop: "0.75rem"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                <User size={12} />
                <span>Logged by: <strong style={{ color: "var(--text-secondary)" }}>{userName}</strong></span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                <Clock size={12} />
                <span>{new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSaving}
                style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
              >
                <Save size={14} /> {isSaving ? "Saving..." : "Save Stock Count"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
